import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Users, 
  User as UserIcon, 
  Activity, 
  MapPin, 
  Video, 
  Calendar as CalendarIcon, 
  Clock, 
  Bell, 
  FileText,
  Search,
  Check,
  ChevronDown,
  Building2,
  AlertCircle,
  X,
  Stethoscope,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const SearchableSelect = ({ label, options, value, onChange, placeholder, icon: Icon, required, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative group" ref={containerRef}>
      <label className="flex justify-between items-center px-1 mb-1.5">
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label} {required && <span className="text-red-500">*</span>}</span>
        {error && <span className="text-[10px] font-black text-red-500 uppercase flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3" /> Required</span>}
      </label>
      <div 
        className={`w-full px-4 py-2.5 bg-white border rounded-xl text-[13px] font-bold flex items-center justify-between transition-all cursor-pointer shadow-sm ${isOpen ? 'ring-4 ring-indigo-500/10 border-indigo-500' : 'border-slate-200 hover:border-slate-300'}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5 overflow-hidden">
          {Icon && <Icon className={`w-3.5 h-3.5 ${isOpen ? 'text-indigo-500' : 'text-slate-400'}`} />}
          <span className={`truncate ${selectedOption ? 'text-slate-800' : 'text-slate-300'}`}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-300 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
          <div className="p-2 border-b border-slate-100 bg-slate-50/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input 
                autoFocus
                type="text" 
                className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:border-indigo-400 placeholder:text-slate-300"
                placeholder={`Search...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto custom-scrollbar">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div 
                  key={opt.value}
                  className={`px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-xs font-bold transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between ${value === opt.value ? 'bg-indigo-50/50 text-indigo-700' : 'text-slate-600'}`}
                  onClick={() => {
                    onChange(opt.value, opt.label);
                    setSearch('');
                    setIsOpen(false);
                  }}
                >
                  {opt.label}
                  {value === opt.value && <Check className="w-3 h-3 text-indigo-600" />}
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-slate-300 text-[10px] font-black uppercase tracking-widest">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const BookAppointment = () => {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPatientList, setShowPatientList] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    doctorId: '',
    doctorName: '',
    clinicalSpecialty: '',
    clinicId: '',
    branch: 'Main',
    isVirtual: false,
    appointmentTypeId: '',
    startTime: '',
    endTime: '',
    location: '',
    reminder: 'None',
    notes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [typesRes, usersRes, clinicsRes] = await Promise.all([
        axios.get(`${window.location.protocol}//${window.location.hostname}/api/appointment-types`, { headers }),
        axios.get(`${window.location.protocol}//${window.location.hostname}/api/users`, { headers }),
        axios.get(`${window.location.protocol}//${window.location.hostname}/api/clinics`, { headers })
      ]);
      setAppointmentTypes(typesRes.data);
      setClinics(clinicsRes.data);
      const docs = usersRes.data.filter(u => u.role === 'doctor' || u.role === 'admin');
      setDoctors(docs);
      if (docs.length > 0) {
        setFormData(prev => ({ ...prev, doctorId: docs[0].id, doctorName: docs[0].username }));
      }
    } catch (err) {
      console.error('Data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const searchPatients = async (q) => {
    if (!q) {
      setPatients([]);
      setShowPatientList(false);
      return;
    }
    try {
      const res = await axios.get(`http://localhost:5000/api/patients/search?q=${q}`, { headers });
      setPatients(res.data.data);
      setShowPatientList(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePatientSelect = (patient) => {
    setFormData({ 
      ...formData, 
      patientId: patient.id, 
      patientName: `${patient.firstName} ${patient.lastName}` 
    });
    setSearchTerm(`${patient.firstName} ${patient.lastName}`);
    setShowPatientList(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patientId || !formData.doctorId || !formData.appointmentTypeId || !formData.clinicId) {
        toast.error('Please fill all mandatory fields');
        return;
    }
    const toastId = toast.loading('Registering appointment...');
    try {
      await axios.post(`${window.location.protocol}//${window.location.hostname}/api/appointments`, formData, { headers });
      toast.success('Appointment booked successfully!', { id: toastId });
      setFormData({
        patientId: '', patientName: '', doctorId: doctors[0]?.id || '', doctorName: doctors[0]?.username || '',
        clinicalSpecialty: '', clinicId: '', branch: 'Main', isVirtual: false,
        appointmentTypeId: '', startTime: '', endTime: '', location: '', reminder: 'None', notes: ''
      });
      setSearchTerm('');
    } catch (err) {
      toast.error('Booking failed: ' + (err.response?.data?.error || err.message), { id: toastId });
    }
  };

  if (loading && doctors.length === 0) return (
    <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
    </div>
  );

  return (
    <div className="p-4 bg-[#F8FAFC] min-h-screen text-slate-700">
      <div className="max-w-5xl mx-auto bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-200/40 relative overflow-hidden">
        
        {/* Subtle Decorative Header Gradient */}
        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500" />
        
        <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between">
           <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center">
                 <Stethoscope className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                 <h2 className="text-lg font-black text-slate-800 tracking-tight leading-none">Schedule Appointment</h2>
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Hospital Management System</p>
              </div>
           </div>
           <div className="flex items-center gap-4">
              <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${formData.isVirtual ? 'bg-purple-50 text-purple-600 border border-purple-100' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'}`}>
                 {formData.isVirtual ? 'Virtual Visit' : 'In-Person Visit'}
              </div>
           </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          
          {/* Main Grid Area */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Box 1: Patient Search (Double width for priority) */}
            <div className="md:col-span-2 relative">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1 mb-1.5 block">Patient Search <span className="text-red-500">*</span></label>
                <div className="relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-indigo-400 transition-colors" />
                    <input 
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all shadow-sm"
                      placeholder="MRN, name, or phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        searchPatients(e.target.value);
                      }}
                      required
                    />
                </div>
                {showPatientList && patients.length > 0 && (
                  <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto animate-in fade-in duration-200 border-t-4 border-t-indigo-500 overflow-x-hidden">
                    {patients.map(p => (
                      <div key={p.id} onClick={() => handlePatientSelect(p)} className="px-5 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 flex items-center justify-between group">
                        <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-black text-slate-400 group-hover:bg-indigo-500 group-hover:text-white transition-all">{p.firstName[0]}</div>
                           <div>
                              <div className="font-bold text-slate-800 text-[13px]">{p.firstName} {p.lastName}</div>
                              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">MRN: {p.medicalRecordNumber || '---'} • {p.dateOfBirth}</div>
                           </div>
                        </div>
                        <Check className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100" />
                      </div>
                    ))}
                  </div>
                )}
            </div>

            {/* Box 2: Virtual Toggle (Smaller, stylized) */}
            <div className="flex flex-col justify-end">
                <div 
                    onClick={() => setFormData({...formData, isVirtual: !formData.isVirtual})}
                    className={`h-[42px] px-4 rounded-xl border border-dashed flex items-center justify-between cursor-pointer transition-all ${formData.isVirtual ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100 scale-[1.02]' : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'}`}
                >
                    <div className="flex items-center gap-2">
                        <Video className={`w-4 h-4 ${formData.isVirtual ? 'text-white' : 'text-slate-300'}`} />
                        <span className="text-[11px] font-black uppercase tracking-widest text-inherit">Virtual Session</span>
                    </div>
                    <div className={`w-8 h-4 rounded-full relative transition-colors ${formData.isVirtual ? 'bg-white/30' : 'bg-slate-100'}`}>
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-all ${formData.isVirtual ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                </div>
            </div>

            {/* Row 2: Logic Info */}
            <SearchableSelect 
              label="Clinic Facility"
              placeholder="Select Clinic..."
              required
              icon={Building2}
              value={formData.clinicId}
              options={clinics.map(c => ({ value: c.id, label: c.name }))}
              onChange={(val, label) => setFormData({ ...formData, clinicId: val, clinicalSpecialty: label })}
            />

            <SearchableSelect 
              label="Practitioner"
              placeholder="Select Staff..."
              required
              icon={UserIcon}
              value={formData.doctorId}
              options={doctors.map(d => ({ value: d.id, label: `${d.username} (${d.role})` }))}
              onChange={(val, label) => setFormData({ ...formData, doctorId: val, doctorName: label })}
            />

            <SearchableSelect 
              label="Session Type"
              placeholder="Select Type..."
              required
              icon={Activity}
              value={formData.appointmentTypeId}
              options={appointmentTypes.map(t => ({ value: t.id, label: t.typeName }))}
              onChange={(val) => setFormData({ ...formData, appointmentTypeId: val })}
            />

            {/* Row 3: Timing (Condensed) */}
            <div className="md:col-span-1 space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Start Time <span className="text-red-500">*</span></label>
                <div className="relative">
                    <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input 
                      type="datetime-local"
                      className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-sm"
                      required
                      value={formData.startTime}
                      onChange={(e) => setFormData({...formData, startTime: e.target.value})}
                    />
                </div>
            </div>

            <div className="md:col-span-1 space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">End Time <span className="text-red-500">*</span></label>
                <div className="relative">
                    <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input 
                      type="datetime-local"
                      className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-sm"
                      required
                      value={formData.endTime}
                      onChange={(e) => setFormData({...formData, endTime: e.target.value})}
                    />
                </div>
            </div>

            <SearchableSelect 
              label="Reminder"
              placeholder="Set Alert..."
              icon={Bell}
              value={formData.reminder}
              options={[
                  { value: 'None', label: 'None' },
                  { value: '15 min before', label: '15 Min' },
                  { value: '30 min before', label: '30 Min' },
                  { value: '1 hour before', label: '1 Hour' },
                  { value: '1 day before', label: '1 Day' }
              ]}
              onChange={(val) => setFormData({ ...formData, reminder: val })}
            />

            {/* Row 4: Single Fields */}
            <div className="md:col-span-1 space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Room / Location</label>
                <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                    <input 
                        type="text"
                        placeholder="Cabin A..."
                        className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-500 transition-all shadow-sm"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                </div>
            </div>

            <div className="md:col-span-2 space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Brief Observation / Notes</label>
                <div className="relative">
                    <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-200" />
                    <textarea 
                      rows="2"
                      className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[13px] font-bold text-slate-700 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all shadow-sm h-[42px] resize-none"
                      placeholder="Reason for visit..."
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    />
                </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-4 border-t border-slate-100">
             <button type="button" onClick={() => {
                setFormData({
                    patientId: '', patientName: '', doctorId: doctors[0]?.id || '', doctorName: doctors[0]?.username || '',
                    clinicalSpecialty: '', clinicId: '', branch: 'Main', isVirtual: false,
                    appointmentTypeId: '', startTime: '', endTime: '', location: '', reminder: 'None', notes: ''
                });
                setSearchTerm('');
             }} className="px-6 py-2 text-[11px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors">Discard</button>
             <button type="submit" className="px-10 py-3 bg-indigo-600 hover:bg-black text-white text-[11px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-100 transition-all flex items-center gap-2 group">
                Register Booking 
                <div className="w-5 h-5 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-indigo-400 transition-all">
                    <Check className="w-3 h-3 text-white" />
                 </div>
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookAppointment;
